/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { App, Modal, Alert, Spin } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import useSocialFeed, { Post } from '@sportninja/common/hooks/useSocialFeed'
import colors from '@sportninja/common/constants/appColors'
import BetaBadge from 'src/components/ts-components/BetaBadge'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import Text from '../ts-components/Text'
import Button from '../ts-components/Button'
import { getSelfUser } from '@sportninja/common/selectors/users'
import { connect } from 'react-redux'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import {
  AudienceBreakdownSection,
  NotificationWarningModal,
  PostHeader,
  CommunicationChannel,
  PostForm,
  PostSettings,
  RecipientsModal,
  type AudienceBreakdownData,
} from './components'
import { SwitchInput } from '../ts-components/SwitchInput'
import { useEntityData } from './hooks/useEntityData'
import { useAudienceCalculator } from './hooks/useAudienceCalculator'
import { initialState, formReducer } from './reducer/useFeedPostReducer'
import { sanitizeContentForSave } from 'src/utils/sanitizeContentForSave'
import { calculateAudienceReach } from './utils/audienceReach'
import {
  OrganizationTreeNode,
  TreeScheduleNode,
} from 'src/context/MyScheduleContext/types'

const styles = css`
  .my-modal-body {
    /* Force scrollbar to always be visible */
    overflow-y: scroll !important;
    position: relative;

    /* Custom scrollbar styling for webkit browsers */
    &::-webkit-scrollbar {
      width: 8px;
      -webkit-appearance: none;
    }

    &::-webkit-scrollbar-track {
      background: ${colors.SECONDARY_400};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.SECONDARY_100};
      border-radius: 4px;
      min-height: 40px;

      &:hover {
        background: ${colors.SECONDARY_75};
      }
    }

    /* Firefox scrollbar styling */
    scrollbar-width: thin;
    scrollbar-color: ${colors.SECONDARY_100} ${colors.SECONDARY_400};
  }
  .my-modal-mask {
    backdrop-filter: blur(10px);
  }
  .my-modal-header {
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: 'my-modal-body',
  mask: 'my-modal-mask',
  header: 'my-modal-header',
  footer: 'my-modal-footer',
  content: 'my-modal-content',
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '0 16px 0 16px',
    maxHeight: 'calc(90vh - 120px)',
    overflowY: 'scroll' as const,
    overflowX: 'hidden' as const,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(10px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
    overflow: 'hidden' as const,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingBottom: '16px',
  },
}

/**
 * @interface FeedPostCreateModalProps
 * @property {boolean} isOpen - Controls the visibility of the modal
 * @property {() => void} onClose - Callback executed when the modal is requested to close
 * @property {string} entityType - The type of the entity the feed belongs to (e.g., 'organization', 'team', 'schedule')
 * @property {string} entityId - The ID of the entity where the post will be created
 * @property {(page: number) => void} refreshNewFeed - Callback to refresh the parent feed view after post creation/update
 * @property {Post | null} postToEdit - The post data for editing. If null, the modal is in "create" mode
 * @property {(post: Post | null) => void} setPostToEdit - Callback to clear the post being edited
 * @property {any} selfUser - Current user data (should be properly typed in the future)
 */
interface FeedPostCreateModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: string
  entityId: string
  refreshNewFeed: (page: number) => void
  postToEdit: Post | null
  setPostToEdit: (post: Post | null) => void
  selfUser: any
  entity?: any
  canTargetAudience: boolean
}

/**
 * A modal component for creating and editing social feed posts.
 *
 * This component provides a comprehensive form interface for users to:
 * - Create new posts with title, content, images, and links
 * - Edit existing posts
 * - Configure post visibility and permissions
 * - Send posts via email and push notifications
 * - Target specific audiences (organizations, teams, schedules)
 *
 * The component uses useReducer for efficient state management and React.memo
 * for performance optimization to prevent unnecessary re-renders.
 *
 * @param props - Component props as defined in FeedPostCreateModalProps
 * @returns JSX element representing the post creation/editing modal
 */
const FeedPostCreateModal: React.FC<FeedPostCreateModalProps> = React.memo(
  ({
    isOpen,
    onClose,
    entityType,
    entityId,
    refreshNewFeed,
    postToEdit,
    setPostToEdit,
    selfUser,
    entity,
    canTargetAudience = false,
  }) => {
    const [formState, dispatch] = useReducer(formReducer, initialState)
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false)
    const [isRecipientsModalOpen, setIsRecipientsModalOpen] = useState(false)
    const [isAudienceBreakdownLoading, setIsAudienceBreakdownLoading] =
      useState(false)
    const [audienceData, setAudienceData] =
      useState<AudienceBreakdownData | null>(null)
    const [audienceError, setAudienceError] = useState<string | null>(null)
    const [showScrollDown, setShowScrollDown] = useState(false)
    const [teamsSearchQuery, setTeamsSearchQuery] = useState('')
    const modalBodyRef = useRef<HTMLDivElement | null>(null)

    // Track scroll position to show/hide the scroll-to-bottom button
    useEffect(() => {
      if (!isOpen) {
        setShowScrollDown(false)
        return
      }

      let scrollHandler: (() => void) | null = null
      let observer: ResizeObserver | null = null
      let body: HTMLDivElement | null = null

      // Small delay to let the modal render and populate
      const timer = setTimeout(() => {
        body = document.querySelector('.my-modal-body') as HTMLDivElement
        if (!body) {
          return
        }
        modalBodyRef.current = body

        const checkScroll = () => {
          if (!body) {
            return
          }
          const { scrollTop, scrollHeight, clientHeight } = body
          const isNearBottom = scrollHeight - scrollTop - clientHeight < 40
          setShowScrollDown(scrollHeight > clientHeight && !isNearBottom)
        }

        scrollHandler = checkScroll
        checkScroll()
        body.addEventListener('scroll', checkScroll, { passive: true })

        observer = new ResizeObserver(checkScroll)
        observer.observe(body)
        if (body.firstElementChild) {
          observer.observe(body.firstElementChild)
        }
      }, 100)

      return () => {
        clearTimeout(timer)
        if (body && scrollHandler) {
          body.removeEventListener('scroll', scrollHandler)
        }
        if (observer) {
          observer.disconnect()
        }
      }
    }, [isOpen])

    const handleScrollToBottom = useCallback(() => {
      if (modalBodyRef.current) {
        modalBodyRef.current.scrollTo({
          top: modalBodyRef.current.scrollHeight,
          behavior: 'smooth',
        })
      }
    }, [])

    // Use entity data hook
    const {
      orgTree: rawOrgTree,
      teams: rawTeams,
      isLoadingEntities,
      hasMoreTeams,
      isLoadingMoreTeams,
      loadMoreTeams,
    } = useEntityData({
      entity,
      isOpen,
      postToEdit,
      teamsSearchQuery,
    })

    /**
     * Recursively search the tree for an org or schedule by id
     */
    const findInTree = useCallback(
      (
        nodes: OrganizationTreeNode[],
        id: string
      ): { found: boolean; type: 'org' | 'sched' } | null => {
        for (const node of nodes) {
          if (node.id === id) {
            return {
              found: true,
              type: node.schedule_type_id != null ? 'sched' : 'org',
            }
          }
          if (node.schedules?.some((s) => s.id === id)) {
            return { found: true, type: 'sched' }
          }
          if (node.children) {
            const result = findInTree(node.children, id)
            if (result) {
              return result
            }
          }
        }
        return null
      },
      []
    )

    // Inject parent entity into tree when editing a post
    const orgTree = useMemo(() => {
      if (
        !postToEdit?.parent_id ||
        !['organization', 'schedule', 'competition'].includes(
          postToEdit?.parent_type
        )
      ) {
        return rawOrgTree
      }
      // Check if already in tree
      if (findInTree(rawOrgTree, postToEdit.parent_id)) {
        return rawOrgTree
      }
      const name = (postToEdit as any).parent?.name || postToEdit.parent_id

      if (postToEdit.parent_type === 'organization') {
        // Prepend synthetic org node
        const syntheticOrg: OrganizationTreeNode = {
          id: postToEdit.parent_id,
          name,
          name_full: name,
          abbreviation: '',
          image_url: null,
          organization_type_id: 0,
          children_count: 0,
        }
        return [syntheticOrg, ...rawOrgTree]
      }
      // schedule/competition: create a synthetic org containing the schedule
      const syntheticSched: TreeScheduleNode = {
        id: postToEdit.parent_id,
        name,
        name_full: name,
        starts_at: '',
        ends_at: '',
        image_url: null,
      }
      const syntheticOrg: OrganizationTreeNode = {
        id: `__synthetic_org_${postToEdit.parent_id}`,
        name: 'Other',
        name_full: 'Other',
        abbreviation: '',
        image_url: null,
        organization_type_id: 0,
        children_count: 0,
        schedules: [syntheticSched],
      }
      return [...rawOrgTree, syntheticOrg]
    }, [rawOrgTree, postToEdit, findInTree])

    // Inject parent team when editing
    const teams = useMemo(() => {
      if (
        !postToEdit?.parent_id ||
        postToEdit?.parent_type !== 'team' ||
        rawTeams.length === 0 ||
        rawTeams.some((t) => t.id === postToEdit.parent_id)
      ) {
        return rawTeams
      }
      const name = (postToEdit as any).parent?.name || postToEdit.parent_id
      return [
        { id: postToEdit.parent_id, name, name_full: name } as any,
        ...rawTeams,
      ]
    }, [rawTeams, postToEdit])

    const isHomePost = entityId === undefined

    /**
     * Helper function to check if HTML content is empty
     * Strips HTML tags and checks if remaining text is empty
     */
    const isContentEmpty = useCallback((content: string) => {
      if (!content) {
        return true
      }
      const text = content.replace(/<[^>]*>/g, '').trim()
      return text.length === 0
    }, [])

    const isSelectedTypeOrg = useMemo(() => {
      // Check if it's an org either from formState or from entityType when entity is pre-selected
      if (
        entity &&
        (entityType === 'organization' ||
          entityType === 'org' ||
          entityType === 'organizations')
      ) {
        return true
      }
      return formState.selectedType === 'org'
    }, [entity, entityType, formState])

    /**
     * Get the currently selected entity with its permissions
     * Similar to getSelectedEntity from TargetAudienceSelector
     */
    const getSelectedEntity = useCallback(() => {
      if (entity) {
        return entity
      }

      if (!formState.selected) {
        return null
      }

      // Search flat teams first
      const team = teams.find((t) => t.id === formState.selected)
      if (team) {
        return team
      }

      // Recursively search org tree for orgs and schedules
      const searchTree = (nodes: OrganizationTreeNode[]): any | null => {
        for (const node of nodes) {
          if (node.id === formState.selected) {
            return node
          }
          const sched = node.schedules?.find((s) => s.id === formState.selected)
          if (sched) {
            return sched
          }
          if (node.children) {
            const found = searchTree(node.children)
            if (found) {
              return found
            }
          }
        }
        return null
      }
      return searchTree(orgTree)
    }, [entity, formState.selected, orgTree, teams])

    /**
     * The /users/me2 endpoints already filter by membership_type=permission,
     * so all returned entities are ones the user can post to.
     * For entity feeds, use the canTargetAudience prop.
     */
    const hasUpdateOrAdminPermission = useMemo(() => {
      if (entity) {
        return canTargetAudience
      }
      return !!getSelectedEntity()
    }, [getSelectedEntity, entity, canTargetAudience])

    /**
     * The ticked channels, as one object.
     *
     * The audience panel, the pre-send confirmation and the recipient list all derive
     * their numbers from this plus the audience payload — no second tally anywhere, which
     * is what keeps the three screens agreeing.
     */
    const selectedChannels = useMemo(
      () => ({
        postToFeed: formState.postToFeed,
        sendViaEmail: formState.sendViaEmail,
        sendViaPush: formState.sendViaPush,
      }),
      [formState.postToFeed, formState.sendViaEmail, formState.sendViaPush]
    )

    /**
     * What those channels actually reach.
     *
     * The panel and the pre-send confirmation each call `calculateAudienceReach` for
     * themselves; this is the third read, and it was the only one recomputed inline in the
     * JSX on every render. Same function, same inputs — one derivation is the whole point.
     */
    const audienceReach = useMemo(
      () =>
        calculateAudienceReach(
          audienceData,
          selectedChannels,
          formState.includeUnjoined
        ),
      [audienceData, selectedChannels, formState.includeUnjoined]
    )

    /*
     * The criteria the recipient list is a view of, named once.
     *
     * The list's props and its remount key below both read these, so they cannot describe
     * two different audiences.
     */
    const recipientRoleTypes = useMemo(
      () => formState.visibleToRoles.map((role) => parseInt(role, 10)),
      [formState.visibleToRoles]
    )

    /*
     * The un-joined only enter the list when Email is what would carry them; with Email
     * unticked the switch is inert, so the list must not widen for it.
     */
    const recipientIncludeUnjoined =
      formState.sendViaEmail && formState.includeUnjoined

    const resolvedEntityId = entity ? entityId : formState.selected

    const resolvedEntityType = useMemo(() => {
      const raw = entity ? entityType : formState.selectedType
      if (!raw) {
        return null
      }
      if (raw.includes('org')) {
        return 'organization' as const
      }
      if (raw.includes('team')) {
        return 'team' as const
      }
      if (raw.includes('sched') || raw.includes('competition')) {
        return 'competition' as const
      }

      return null
    }, [entity, entityType, formState.selectedType])

    // Hidden — not disabled — for anyone who can preview but not send. The recipients
    // endpoint returns personal email addresses and is gated on the SEND authority, so
    // offering the link to a preview-only user would only hand them a 403.
    const canViewRecipients =
      hasUpdateOrAdminPermission &&
      !!resolvedEntityId &&
      !!resolvedEntityType &&
      formState.hasCalculatedAudience

    const openRecipients = useCallback(() => {
      setIsRecipientsModalOpen(true)
    }, [])

    const { message } = App.useApp()

    const { createPost, updatePost } = useSocialFeed({
      id: entityId || formState.parentId || undefined,
      type: entityType as 'team' | 'schedule' | 'organization' | undefined,
      shouldFetch: false,
    })

    const handleToggleSection = (section: 'image' | 'link' | 'sendVia') => {
      dispatch({ type: 'TOGGLE_SECTION', section })
    }

    const handleSetSelected = useCallback(
      (value: any) => {
        dispatch({
          type: 'SET_FIELD',
          field: 'selected',
          payload: value,
        })
        // Clear audience data when entity changes
        if (value !== formState.selected) {
          setAudienceData(null)
        }
      },
      [formState.selected]
    )

    const handleSetSelectedType = useCallback((value: string | null) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'selectedType',
        payload: value,
      })
    }, [])

    const handleSetTargetAudience = useCallback((value: number | null) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'targetAudience',
        payload: value,
      })
    }, [])

    const handleSetParentId = useCallback((value: string) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'parentId',
        payload: value,
      })
    }, [])

    const handleSetParentEntityType = useCallback(
      (value: 'team' | 'schedule' | 'organization' | undefined) => {
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: value,
        })
      },
      []
    )

    const handleAudienceDataChange = useCallback(
      (data: AudienceBreakdownData | null) => {
        setAudienceData(data)
        if (data) {
          dispatch({
            type: 'SET_FIELD',
            field: 'hasCalculatedAudience',
            payload: true,
          })
        }
      },
      []
    )

    // Use audience calculator hook
    const { calculateAudience } = useAudienceCalculator({
      selectedEntityId: entity ? entityId : formState.selected,
      selectedEntityType: entity
        ? entityType === 'organization'
          ? 'org'
          : entityType === 'schedule'
          ? 'sched'
          : entityType
        : formState.selectedType,
      activeCompetition: formState.activeCompetition,
      visibleToRoles: formState.visibleToRoles,
      onLoadingChange: setIsAudienceBreakdownLoading,
      onDataChange: handleAudienceDataChange,
      onError: setAudienceError,
    })

    useEffect(() => {
      const orgTreeEmpty = orgTree.length === 0
      const teamEmpty = teams.length === 0
      if (!orgTreeEmpty && !teamEmpty) {
        return
      }
      if (orgTreeEmpty && !teamEmpty) {
        dispatch({ type: 'SET_FIELD', field: 'selected', payload: teams[0].id })
        dispatch({ type: 'SET_FIELD', field: 'selectedType', payload: 'team' })
        dispatch({ type: 'SET_FIELD', field: 'parentId', payload: teams[0].id })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: 'team',
        })
      }
      if (!orgTreeEmpty && teamEmpty) {
        const firstNode = orgTree[0]
        const isCompetition = firstNode.schedule_type_id != null
        dispatch({
          type: 'SET_FIELD',
          field: 'selected',
          payload: firstNode.id,
        })
        dispatch({
          type: 'SET_FIELD',
          field: 'selectedType',
          payload: isCompetition ? 'sched' : 'org',
        })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentId',
          payload: firstNode.id,
        })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: isCompetition ? 'schedule' : 'organization',
        })
      }
    }, [orgTree, teams, isOpen])

    // Scroll modal body to top when opened
    useEffect(() => {
      if (isOpen) {
        setTimeout(() => {
          const modalBody = document.querySelector('.my-modal-body')
          if (modalBody) {
            modalBody.scrollTop = 0
          }
        }, 0)
      }
    }, [isOpen])

    useEffect(() => {
      dispatch({ type: 'SET_POST_TO_EDIT', payload: postToEdit })
    }, [postToEdit])

    // Auto-calculate audience when editing a post (wait for entities to load)
    useEffect(() => {
      if (
        postToEdit &&
        isOpen &&
        !isLoadingEntities &&
        (entityId || formState.selected)
      ) {
        calculateAudience()
      }
    }, [postToEdit, isOpen, isLoadingEntities]) // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize selectedType when entity is pre-selected
    useEffect(() => {
      if (entity && entityType) {
        // Map entityType to the format used in selectedType
        let mappedType = entityType
        if (entityType === 'organization') {
          mappedType = 'org'
        } else if (entityType === 'schedule') {
          mappedType = 'sched'
        }

        dispatch({
          type: 'SET_FIELD',
          field: 'selectedType',
          payload: mappedType,
        })

        // Also set the selected field and parent fields
        if (entityId) {
          dispatch({
            type: 'SET_FIELD',
            field: 'selected',
            payload: entityId,
          })
          dispatch({
            type: 'SET_FIELD',
            field: 'parentId',
            payload: entityId,
          })
          dispatch({
            type: 'SET_FIELD',
            field: 'parentEntityType',
            payload: entityType as 'team' | 'schedule' | 'organization',
          })
        }
      }
    }, [entity, entityType, entityId])

    // Auto-enable activeCompetition when org is selected
    useEffect(() => {
      if (
        formState.selectedType === 'org' ||
        (entity && entityType === 'organization')
      ) {
        dispatch({
          type: 'SET_FIELD',
          field: 'activeCompetition',
          payload: true,
        })
      }
    }, [formState.selectedType, entity, entityType])

    const performSubmit = useCallback(async () => {
      try {
        dispatch({ type: 'SET_FIELD', field: 'isLoading', payload: true })
        const { postToFeed, sendViaEmail, sendViaPush } = formState

        let entityTypeToSubmit = formState.parentEntityType || entityType
        if (entityTypeToSubmit === 'sched') {
          entityTypeToSubmit = 'schedule'
        }
        if (entityTypeToSubmit === 'org') {
          entityTypeToSubmit = 'organization'
        }
        const resolvedUrl =
          formState.url &&
          !formState.url?.startsWith('http') &&
          formState.url?.length > 0 &&
          formState.url?.includes('.')
            ? `https://${formState.url}`
            : formState.url

        const postData = {
          send_social_feed: !!postToFeed,
          send_email: !!sendViaEmail,
          send_push_notification: !!sendViaPush,
          can_reply: formState.canReply,
          active_competition: formState.activeCompetition,
          // Only meaningful alongside send_email; the backend gates on both, and sending
          // it unconditionally keeps the stored post an honest record of what was chosen.
          include_unjoined: formState.includeUnjoined,
          title: formState.title,
          body: sanitizeContentForSave(formState.content),
          body_plain_text: formState.body_plain_text,
          image: formState.image,
          role_types: formState.visibleToRoles.map((role) =>
            parseInt(role, 10)
          ),
          url: resolvedUrl,
        } as any

        if (postToEdit) {
          await updatePost(postData, postToEdit.id, formState.imageChanged)
        } else {
          await createPost(
            postData,
            entityTypeToSubmit as 'team' | 'schedule' | 'organization'
          )
        }
        message.open({
          type: 'success',
          content: postToEdit
            ? 'Communication Updated Successfully'
            : 'Communication Created Successfully',
        })
        dispatch({ type: 'RESET_FORM' })
        refreshNewFeed(1)
        setPostToEdit(null)
        setAudienceData(null) // Clear audience data after successful submission
        onClose()
      } catch (e: any) {
        if (e?.invalid_fields) {
          const newFieldErrors = [...formState.fieldErrors]
          const newCustomError = { ...formState.customError }

          if (e?.invalid_fields?.url) {
            newFieldErrors.push('website')
            newCustomError.website = e?.invalid_fields?.url
          }

          if (e?.invalid_fields?.role_types) {
            newFieldErrors.push('visibleToRoles')
            newCustomError.visibleToRoles = e?.invalid_fields?.role_types
          }

          if (newFieldErrors.length > formState.fieldErrors.length) {
            dispatch({
              type: 'SET_VALIDATION_ERRORS',
              fieldErrors: newFieldErrors,
              customError: newCustomError,
            })
            return
          }
        }
        const errorMessage = getErrorMessage(e)
        dispatch({ type: 'SET_FIELD', field: 'error', payload: errorMessage })
      } finally {
        dispatch({ type: 'SET_FIELD', field: 'isLoading', payload: false })
      }
    }, [
      formState,
      entityType,
      postToEdit,
      message,
      refreshNewFeed,
      setPostToEdit,
      onClose,
      updatePost,
      createPost,
    ])

    const handleSubmit = useCallback(async () => {
      dispatch({ type: 'SET_FIELD', field: 'error', payload: null })

      // Validation: check required fields before submit
      const newFieldErrors: string[] = []
      const newCustomError: { [key: string]: string } = {
        ...formState.customError,
      }

      if (formState.title.length === 0) {
        newFieldErrors.push('title')
        newCustomError.title = 'Title is required'
      } else {
        newCustomError.title = ''
      }

      if (isContentEmpty(formState.content)) {
        newFieldErrors.push('content')
        newCustomError.content = 'Content is required'
      } else {
        newCustomError.content = ''
      }

      if (isHomePost && (!formState.parentEntityType || !formState.parentId)) {
        newFieldErrors.push('selectedEntity')
        newCustomError.selectedEntity = 'Audience is required'
      } else {
        newCustomError.selectedEntity = ''
      }

      if (
        formState.selected &&
        !formState.postToFeed &&
        !formState.sendViaEmail &&
        !formState.sendViaPush
      ) {
        newFieldErrors.push('channels')
        newCustomError.channels = 'At least one channel must be selected'
      } else {
        newCustomError.channels = ''
      }

      dispatch({
        type: 'SET_VALIDATION_ERRORS',
        fieldErrors: newFieldErrors,
        customError: newCustomError,
      })

      if (newFieldErrors.length > 0) {
        return
      }

      // Check if notifications are enabled and show warning modal
      if (formState.sendViaEmail || formState.sendViaPush) {
        await calculateAudience()
        setIsWarningModalOpen(true)
        return
      }

      // Proceed with submission if no notifications
      await performSubmit()
    }, [
      calculateAudience,
      formState.content,
      formState.selected,
      formState.postToFeed,
      formState.sendViaEmail,
      formState.customError,
      formState.sendViaPush,
      formState.title.length,
      formState.parentEntityType,
      formState.parentId,
      isHomePost,
      isContentEmpty,
      performSubmit,
    ])

    const handleWarningModalClose = useCallback(() => {
      setIsWarningModalOpen(false)
    }, [])

    const handleWarningModalConfirm = useCallback(async () => {
      setIsWarningModalOpen(false)
      await performSubmit()
    }, [performSubmit])

    const getHeader = useCallback(() => {
      return (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 16px;
          `}
        >
          <Text
            variant='heading3'
            weight='semiBold'
            color={colors.WHITE}
            textTransform='capitalize'
          >
            {postToEdit ? 'Edit Communication' : 'New Communication'}
          </Text>
          {/* Shared badge, not antd's <Tag color={hex}>: Tag treats the hex as
              a background and picks its own text color, always white — which
              is 1.86:1 on the SportNinja amber. BetaBadge reads the content
              color from PRIMARY_BUTTON_CONTENT so it tracks the fill. */}
          <BetaBadge
            css={css`
              margin-top: 4px;
            `}
          />
        </div>
      )
    }, [postToEdit])

    const closeIcon = useCallback(() => {
      return (
        <CloseOutlined
          css={css`
            color: ${colors.SOLITUDE};
            font-size: 16px;
          `}
        />
      )
    }, [])

    const handleClose = useCallback(() => {
      dispatch({ type: 'RESET_FORM' })
      setPostToEdit(null)
      setAudienceData(null) // Clear audience data when closing modal
      setTeamsSearchQuery('')
      onClose()
    }, [onClose, setPostToEdit])

    const getFooter = useCallback(() => {
      return (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            width: 50%;
            margin-left: auto;
          `}
        >
          <Button
            text='Cancel'
            onClick={handleClose}
            variant='secondary'
            disabled={formState.isLoading || isLoadingEntities}
          />
          <Button
            text='Save'
            onClick={handleSubmit}
            disabled={
              formState.isLoading ||
              isLoadingEntities ||
              isAudienceBreakdownLoading
            }
            isLoading={formState.isLoading}
            variant='primary'
          />
        </div>
      )
    }, [
      formState.isLoading,
      handleClose,
      handleSubmit,
      isAudienceBreakdownLoading,
      isLoadingEntities,
    ])

    return (
      <Modal
        css={styles}
        footer={getFooter()}
        closeIcon={closeIcon()}
        title={getHeader()}
        open={isOpen}
        classNames={classNames}
        styles={modalStyles}
        width={900}
        centered
        onCancel={handleClose}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            position: relative;
            padding-bottom: 16px;
            padding-top: 16px;
          `}
        >
          {/* Show loading spinner when loading entities in edit mode */}
          {isLoadingEntities && postToEdit && (
            <div
              css={css`
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(39, 48, 62, 0.95);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 10;
              `}
            >
              <Spin size='small' />
            </div>
          )}
          <PostHeader
            selfUser={selfUser}
            entity={entity}
            entityType={entityType}
            orgTree={orgTree}
            teams={teams}
            selected={formState.selected}
            setSelected={handleSetSelected}
            selectedType={formState.selectedType}
            setSelectedType={handleSetSelectedType}
            targetAudience={formState.targetAudience}
            setTargetAudience={handleSetTargetAudience}
            setParentId={handleSetParentId}
            setParentEntityType={handleSetParentEntityType}
            isLoading={formState.isLoading}
            postToEdit={postToEdit}
            isLoadingEntities={isLoadingEntities}
            showImage={formState.showImage}
            showLink={formState.showLink}
            showSendVia={formState.showSendVia}
            onToggleSection={handleToggleSection}
            name='selectedEntity'
            fieldErrors={formState.fieldErrors}
            customError={formState.customError}
            hasMoreTeams={hasMoreTeams}
            isLoadingMoreTeams={isLoadingMoreTeams}
            onLoadMoreTeams={loadMoreTeams}
            onTeamsSearch={setTeamsSearchQuery}
          />

          {/* Side-by-side layout: Post Settings (left) and Audience Breakdown (right) */}
          <div
            css={css`
              display: flex;
              gap: 24px;
              align-self: stretch;
              width: 100%;
              margin-bottom: 16px;
            `}
          >
            {/* Left side - Target Audience & Active Competition */}
            <div
              css={css`
                flex: 1;
                min-width: 0;
              `}
            >
              <PostSettings
                activeCompetition={formState.activeCompetition}
                dispatch={dispatch}
                isSelectedTypeOrg={isSelectedTypeOrg}
                hasUpdateOrAdminPermission={hasUpdateOrAdminPermission}
                hasAudience={!!(formState.selected || entity)}
                visibleToRoles={formState.visibleToRoles}
                fieldErrors={formState.fieldErrors}
                customError={formState.customError}
              />
            </div>

            {/* Vertical Divider */}
            <div
              css={css`
                width: 1px;
                background: ${colors.SECONDARY_100};
                align-self: stretch;
              `}
            />

            {/* Right side - Audience Breakdown */}
            <div
              css={css`
                flex: 1;
                min-width: 0;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                ${!formState.selected && !entity
                  ? 'opacity: 0.4; pointer-events: none;'
                  : ''}
              `}
            >
              <AudienceBreakdownSection
                selectedEntityId={entity ? entityId : formState.selected}
                selectedEntityType={
                  entity
                    ? entityType === 'organization' ||
                      entityType === 'org' ||
                      entityType === 'organizations'
                      ? 'org'
                      : entityType === 'schedule'
                      ? 'sched'
                      : entityType
                    : formState.selectedType
                }
                audienceData={audienceData}
                onCalculate={calculateAudience}
                isCalculating={isAudienceBreakdownLoading}
                hasCalculated={formState.hasCalculatedAudience}
                error={audienceError}
                isCalculateDisabled={
                  !formState.visibleToRoles ||
                  formState.visibleToRoles.length === 0
                }
                channels={selectedChannels}
                includeUnjoined={formState.includeUnjoined}
                onViewRecipients={
                  canViewRecipients ? openRecipients : undefined
                }
              />
            </div>
          </div>

          {/* Horizontal Divider */}
          <div
            css={css`
              width: 100%;
              height: 1px;
              background: ${colors.SECONDARY_100};
              margin-bottom: 16px;
            `}
          />

          {/* Communication Channels (row layout) */}
          {!postToEdit && (
            <div
              css={css`
                align-self: stretch;
                margin-bottom: 16px;
              `}
            >
              <CommunicationChannel
                postToFeed={formState.postToFeed}
                sendViaEmail={formState.sendViaEmail}
                sendViaPush={formState.sendViaPush}
                includeUnjoined={formState.includeUnjoined}
                unjoinedAvailable={
                  formState.hasCalculatedAudience && audienceData
                    ? audienceData.unjoined?.emailable ?? 0
                    : null
                }
                dispatch={dispatch}
                name='channels'
                fieldErrors={formState.fieldErrors}
                customError={formState.customError}
              />
            </div>
          )}

          {/* Allow Comments - Only visible when posting to social feed */}
          {formState.postToFeed && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                align-self: stretch;
                width: 100%;
                margin-bottom: 16px;
              `}
            >
              <SwitchInput
                title='Allow Comments'
                subtitle='Let others join the conversation on this post'
                value={formState.canReply}
                onChange={(value) =>
                  dispatch({
                    type: 'SET_FIELD',
                    field: 'canReply',
                    payload: value,
                  })
                }
              />
            </div>
          )}

          <PostForm
            title={formState.title}
            content={formState.content}
            url={formState.url}
            image={formState.image}
            isLoading={formState.isLoading}
            showImage={formState.showImage}
            showLink={formState.showLink}
            fieldErrors={formState.fieldErrors}
            customError={formState.customError}
            dispatch={dispatch}
          />

          {formState.error && (
            <Alert
              message={formState.error}
              type='error'
              css={css`
                margin-top: 16px;
              `}
            />
          )}

          {/* Scroll-to-bottom indicator */}
          {showScrollDown && (
            <div
              css={css`
                position: sticky;
                bottom: 16px;
                display: flex;
                justify-content: flex-end;
                pointer-events: none;
                z-index: 10;
              `}
            >
              <button
                type='button'
                onClick={handleScrollToBottom}
                css={css`
                  pointer-events: auto;
                  width: 36px;
                  height: 36px;
                  border-radius: 50%;
                  border: none;
                  background: ${colors.PRIMARY};
                  color: ${PRIMARY_BUTTON_CONTENT.icon};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                  transition: transform 0.2s ease;

                  &:hover {
                    transform: scale(1.1);
                  }
                `}
                aria-label='Scroll to bottom'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M8 3v10m0 0l4-4m-4 4L4 9'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
        <NotificationWarningModal
          isOpen={isWarningModalOpen}
          onClose={handleWarningModalClose}
          onConfirm={handleWarningModalConfirm}
          isLoading={formState.isLoading}
          audienceData={audienceData}
          isLoadingAudience={isAudienceBreakdownLoading}
          channels={selectedChannels}
          includeUnjoined={formState.includeUnjoined}
          onViewRecipients={canViewRecipients ? openRecipients : undefined}
        />
        {/*
          Rendered whenever an entity is resolved, not gated on `isRecipientsModalOpen` —
          unmounting it on close skips the modal's exit transition. Its query is gated on
          `isOpen`, so a closed modal costs nothing.
        */}
        {resolvedEntityType && (
          /*
           * KEYED ON THE CRITERIA, so a change to them remounts the list.
           *
           * `page`, `status` and `search` are `useState` inside a component that stays
           * mounted (see above), so they outlive a criteria change: filter to "Not
           * Invited", page to 3, close, retarget the roles, re-calculate, re-open — and
           * the reader is still on page 3 of a filter, now against a smaller set. The
           * server returns no rows for an out-of-range page while `pagination.total`
           * still reports the full count, so that lands on "No rows" under
           * "Showing 0 of 40".
           *
           * A remount resets all three, AND `SearchInput`'s own `searchTerm` — that last
           * one is why this is a key rather than an effect: `SearchInput` is uncontrolled
           * (`{ onSearch, placeholder, disabled }`, no `value`), so nothing short of a
           * remount clears the text the reader can still see.
           *
           * The key cannot change while the modal is open — its mask blocks the form
           * behind it — so the exit transition is untouched. Reopening on UNCHANGED
           * criteria keeps the filter and page on purpose: same set, nothing stale.
           */
          <RecipientsModal
            key={`${resolvedEntityId}|${resolvedEntityType}|${recipientRoleTypes.join(
              ','
            )}|${formState.activeCompetition}|${recipientIncludeUnjoined}`}
            isOpen={isRecipientsModalOpen}
            onClose={() => setIsRecipientsModalOpen(false)}
            entityId={resolvedEntityId}
            entityType={resolvedEntityType}
            entityName={getSelectedEntity()?.name}
            roleTypes={recipientRoleTypes}
            activeCompetition={formState.activeCompetition}
            includeUnjoined={recipientIncludeUnjoined}
            reachTotal={audienceReach.total}
            audienceTotal={audienceReach.inAudience}
          />
        )}
      </Modal>
    )
  }
)

const makeMapStateToProps = () => {
  return (state) => {
    return {
      selfUser: getSelfUser(state),
    }
  }
}
const mapDispatchToProps = (_dispatch) => ({})

export default connect(
  makeMapStateToProps,
  mapDispatchToProps
)(FeedPostCreateModal)
